/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { useMemo, useState } from 'react'

import { Activity, ActivityFilter } from '@/components/quality-explorer/types'

/** A description of the return type for this hook. */
type UseActivitiesFiltersReturn = {
  /** The filters available. */
  filters: ActivityFilter

  /** Callback for amending the selected filters. */
  setFilters: (key: keyof ActivityFilter, value: string) => void

  /** An array of activities that have been filtered based on selections. */
  filteredActivities: Array<Activity>
}

/**
 * A custom hook to handle activity filtering logic in the qualities explorer.
 *
 * @param allActivities - the full list of activities to filter
 */
export const useActivitiesFilters = (allActivities: Array<Activity>): UseActivitiesFiltersReturn => {
  const [filters, setFilters] = useState<ActivityFilter>({})

  const handleFilterChange = (key: keyof ActivityFilter, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }))
  }

  const filteredActivities = useMemo(() => {
    const filterKeys = (Object.keys(filters) as Array<keyof ActivityFilter>).filter((key) => filters[key])

    if (filterKeys.length === 0) return allActivities

    return allActivities.filter((activity: Activity) => {
      if (!activity.data) return false

      const data = Object.fromEntries(
        activity.data
          .split(',')
          .map((item) => item.split(':'))
          .filter(([key, value]) => key && value)
          .map(([key, value]) => [key.trim(), value.trim()]),
      ) as Record<keyof ActivityFilter, string>

      return filterKeys.every((key) => data[key] === filters[key])
    })
  }, [allActivities, filters])

  return {
    filteredActivities,
    filters,
    setFilters: handleFilterChange,
  }
}
